import type { EconomyNaming } from '@shared';
import { useSession } from '../../state/session';
import { Badge } from '../../components/ui/Badge';
import { Icon, type IconName } from '../../components/ui/Icon';
import { creditIconFor } from '../../components/ui/currency';
import type { SegmentReward } from '../types';

type Tone = 'brand' | 'success' | 'warning' | 'danger' | 'neutral';

const CONFIG: Record<SegmentReward, { tone: Tone; label: string; icon: IconName }> = {
  credits: { tone: 'warning', label: 'Credits', icon: 'coins' },
  xp: { tone: 'brand', label: 'XP', icon: 'bolt' },
  role: { tone: 'success', label: 'Role', icon: 'shield' },
  custom: { tone: 'danger', label: 'Custom', icon: 'gift' },
  coupon: { tone: 'success', label: 'Coupon', icon: 'ticket' },
  respin: { tone: 'brand', label: 'Respin', icon: 'refresh' },
  nothing: { tone: 'neutral', label: 'Nothing', icon: 'ban' },
};

/**
 * `naming` is optional because this badge has two audiences.
 *
 * In the console it labels a wheel slice, where the house words are the right
 * ones — an owner tuning six wheels reads "Credits" faster than six different
 * currencies. In the player's spin history it is the client's own realm, and a
 * badge still reading "Credits" beside a Moonpetal balance is the one place the
 * skin visibly comes off. So the caller says which it is.
 */
export function RewardBadge({ reward, naming }: { reward: SegmentReward; naming?: EconomyNaming }) {
  const { theme } = useSession();
  const { tone, label, icon } = CONFIG[reward];
  // The mark follows the realm even in the console: the owner is looking at her
  // own profile there, so her own currency is the right one to show.
  const marked = reward === 'credits' ? creditIconFor(theme) : icon;
  const worded =
    naming && reward === 'credits'
      ? naming.creditNamePlural
      : naming && reward === 'xp'
        ? naming.xpName
        : label;

  return (
    <Badge tone={tone}>
      <Icon name={marked} size={12} />
      {worded}
    </Badge>
  );
}
